import { useEffect, useRef, useState } from 'react';
import {
  COUNTDOWN_DURATION_SECONDS,
  SNOOZE_DURATION_MS,
  UPDATE_TOAST_ID,
  VERSION_POLL_INTERVAL_MS,
  VERSION_RELOAD_OVERRIDE_KEY,
  VERSION_URL,
} from '@/components/version-reload/constants';
import showVersionReloadToast from '@/components/version-reload/showVersionReloadToast';
import { appVersion, enableVersionReloadByDefault, simulateVersionUpdate } from '@/config';
import toast from 'react-hot-toast';

interface VersionResponse {
  version?: string;
}

function reloadToLatestVersion() {
  window.location.reload();
}

async function fetchLatestVersion() {
  const response = await fetch(VERSION_URL, { cache: 'no-store' });

  if (!response.ok) {
    return null;
  }

  return (await response.json()) as VersionResponse;
}

export default function VersionReloadController() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(COUNTDOWN_DURATION_SECONDS);
  const [isCountdownRunning, setIsCountdownRunning] = useState(false);
  const snoozedUntilRef = useRef(0);
  const countdownIntervalIdRef = useRef<number | null>(null);
  const updateAvailableRef = useRef(false);
  const isCountdownRunningRef = useRef(false);
  const isRefreshingRef = useRef(false);

  const refreshToLatestVersion = () => {
    if (isRefreshingRef.current) {
      return;
    }

    isRefreshingRef.current = true;
    if (countdownIntervalIdRef.current != null) {
      window.clearInterval(countdownIntervalIdRef.current);
      countdownIntervalIdRef.current = null;
    }
    updateAvailableRef.current = false;
    isCountdownRunningRef.current = false;
    setUpdateAvailable(false);
    setIsCountdownRunning(false);
    setCountdownSeconds(COUNTDOWN_DURATION_SECONDS);
    toast.remove(UPDATE_TOAST_ID);
    reloadToLatestVersion();
  };

  useEffect(() => {
    let isDisposed = false;
    const versionReloadEnabled =
      enableVersionReloadByDefault ||
      window.localStorage.getItem(VERSION_RELOAD_OVERRIDE_KEY) === 'true';

    if (!versionReloadEnabled) {
      toast.remove(UPDATE_TOAST_ID);
      return;
    }

    const clearCountdownInterval = () => {
      if (countdownIntervalIdRef.current != null) {
        window.clearInterval(countdownIntervalIdRef.current);
        countdownIntervalIdRef.current = null;
      }
    };

    const stopCountdown = () => {
      clearCountdownInterval();
      isCountdownRunningRef.current = false;
      setIsCountdownRunning(false);
      setCountdownSeconds(COUNTDOWN_DURATION_SECONDS);
    };

    const startCountdown = () => {
      if (!updateAvailableRef.current || document.hidden || isCountdownRunningRef.current) {
        return;
      }

      isCountdownRunningRef.current = true;
      setIsCountdownRunning(true);
      setCountdownSeconds(COUNTDOWN_DURATION_SECONDS);
      clearCountdownInterval();

      countdownIntervalIdRef.current = window.setInterval(() => {
        setCountdownSeconds((currentSeconds) => {
          if (currentSeconds <= 1) {
            refreshToLatestVersion();
            return 0;
          }

          return currentSeconds - 1;
        });
      }, 1_000);
    };

    const setUpdatePending = () => {
      if (Date.now() < snoozedUntilRef.current) {
        return;
      }

      isRefreshingRef.current = false;

      if (updateAvailableRef.current) {
        return;
      }

      updateAvailableRef.current = true;
      setUpdateAvailable(true);
      startCountdown();
    };

    const checkVersion = async () => {
      if (document.hidden) {
        return;
      }

      if (simulateVersionUpdate) {
        setUpdatePending();
        return;
      }

      try {
        const latestVersion = await fetchLatestVersion();

        if (isDisposed || latestVersion?.version == null) {
          return;
        }

        if (latestVersion.version !== appVersion) {
          console.info('New version detected.', {
            currentVersion: appVersion,
            latestVersion: latestVersion.version,
          });
          setUpdatePending();
        }
      } catch {
        // Ignore transient network and CDN errors and retry on the next poll.
      }
    };

    void checkVersion();
    const intervalId = window.setInterval(() => {
      void checkVersion();
    }, VERSION_POLL_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        if (updateAvailableRef.current && Date.now() >= snoozedUntilRef.current) {
          setUpdateAvailable(true);
          startCountdown();
        }
        void checkVersion();
        return;
      }

      stopCountdown();
    };

    const handleUserActivity = () => {
      if (!updateAvailableRef.current) {
        return;
      }

      if (isCountdownRunningRef.current) {
        stopCountdown();
      }

      if (Date.now() >= snoozedUntilRef.current) {
        startCountdown();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pointerdown', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);
    window.addEventListener('scroll', handleUserActivity, true);
    window.addEventListener('touchstart', handleUserActivity);

    return () => {
      isDisposed = true;
      clearCountdownInterval();
      toast.remove(UPDATE_TOAST_ID);
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pointerdown', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('scroll', handleUserActivity, true);
      window.removeEventListener('touchstart', handleUserActivity);
    };
  }, []);

  useEffect(() => {
    if (!updateAvailable || Date.now() < snoozedUntilRef.current) {
      toast.remove(UPDATE_TOAST_ID);
      return;
    }

    showVersionReloadToast({
      countdownSeconds,
      isCountingDown: isCountdownRunning,
      onLater: () => {
        snoozedUntilRef.current = Date.now() + SNOOZE_DURATION_MS;
        updateAvailableRef.current = false;
        isCountdownRunningRef.current = false;
        if (countdownIntervalIdRef.current != null) {
          window.clearInterval(countdownIntervalIdRef.current);
          countdownIntervalIdRef.current = null;
        }
        setUpdateAvailable(false);
        setIsCountdownRunning(false);
        setCountdownSeconds(COUNTDOWN_DURATION_SECONDS);
      },
      onRefresh: refreshToLatestVersion,
    });
  }, [countdownSeconds, isCountdownRunning, updateAvailable]);

  return null;
}
